import React, { useState, useEffect, useRef } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Highlighter, Check } from 'lucide-react';
import { Button } from './button';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './select';
import { Badge } from './badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

interface SelectOption {
  value: string;
  label: string;
}

interface SelectOptionGroup {
  label: string;
  options: SelectOption[];
}

interface HighlightableSelectProps {
  value?: string;
  onChange?: (value: string) => void;
  onValueChange?: (value: string) => void; // Alternative prop name
  placeholder?: string;
  options?: (SelectOption | SelectOptionGroup)[];
  className?: string;
  disabled?: boolean;
  isHighlighted?: boolean;
  onHighlightChange?: (highlighted: boolean) => void;
  fieldName?: string; // Field name for storing highlight state
  form?: UseFormReturn<any>; // Form instance for centralized state
}

export function HighlightableSelect({
  value,
  onChange,
  onValueChange,
  placeholder = 'Select an option',
  options = [],
  className,
  disabled = false,
  isHighlighted: propIsHighlighted,
  onHighlightChange,
  fieldName,
  form,
}: HighlightableSelectProps) {
  // Support both onChange and onValueChange props
  const handleChange = onChange || onValueChange;
  
  // Initialize highlighted state from form if available
  const [localIsHighlighted, setLocalIsHighlighted] = useState(() => {
    if (fieldName && form) {
      const highlightedFields = form.getValues('highlightedFields') || {};
      return highlightedFields[fieldName] || false;
    }
    return propIsHighlighted || false;
  });

  const isHighlighted = propIsHighlighted !== undefined ? propIsHighlighted : localIsHighlighted;
  const hasValue = !!value;

  // Use a ref to track the current highlight state to avoid stale closures in subscription
  const localIsHighlightedRef = useRef(localIsHighlighted);
  useEffect(() => {
    localIsHighlightedRef.current = localIsHighlighted;
  }, [localIsHighlighted]);

  // Subscribe to form changes properly using form.watch() with callback
  useEffect(() => {
    if (!fieldName || !form) return;

    // Function to sync highlight state from form
    const syncFromForm = () => {
      // Always read fresh from form.getValues() to ensure we get latest data
      const highlightedFields = form.getValues('highlightedFields') || {};
      const formHighlightState = !!highlightedFields[fieldName];
      if (formHighlightState !== localIsHighlightedRef.current) {
        setLocalIsHighlighted(formHighlightState);
      }
    };

    // Initial sync
    syncFromForm();

    // Subscribe to all form changes - callback fires on any form value change
    const subscription = form.watch((data, { name }) => {
      // Sync on full form update (name is undefined, e.g., from form.reset())
      // or when highlightedFields specifically changes
      if (!name || name === 'highlightedFields' || name.startsWith('highlightedFields.')) {
        syncFromForm();
      }
    });

    return () => subscription.unsubscribe();
  }, [fieldName, form]);

  const handleSelectChange = (newValue: string) => {
    handleChange?.(newValue);
  };

  const handleHighlightToggle = () => {
    if (!hasValue) return;
    
    const newHighlightState = !isHighlighted;
    
    // Update local state
    setLocalIsHighlighted(newHighlightState);
    
    // Update form state if available
    if (fieldName && form) {
      const currentHighlights = form.getValues('highlightedFields') || {};
      const updatedHighlights = { ...currentHighlights, [fieldName]: newHighlightState };
      form.setValue('highlightedFields', updatedHighlights);
    }
    
    // Call the callback if provided
    onHighlightChange?.(newHighlightState);
  };

  // Check if option is a group
  const isOptionGroup = (option: SelectOption | SelectOptionGroup): option is SelectOptionGroup => {
    return 'options' in option;
  };

  return (
    <div className="space-y-1.5">
      <div className="flex gap-2">
        <Select
          value={value}
          onValueChange={handleSelectChange}
          disabled={disabled}
        >
          <SelectTrigger 
            className={cn(
              "flex-1",
              isHighlighted && "border-yellow-400 bg-yellow-50 dark:bg-yellow-950/20",
              className
            )}
          >
            <div className="flex items-center justify-between w-full gap-2">
              <SelectValue placeholder={placeholder} />
              {isHighlighted && (
                <Badge 
                  variant="secondary" 
                  className="bg-yellow-200 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200 text-xs px-1.5 py-0"
                >
                  Highlighted
                </Badge>
              )}
            </div>
          </SelectTrigger>
          <SelectContent>
            {options.map((option, index) => {
              if (isOptionGroup(option)) {
                return (
                  <SelectGroup key={index}>
                    <SelectLabel>{option.label}</SelectLabel>
                    {option.options.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                );
              } else {
                return (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                );
              }
            })}
          </SelectContent>
        </Select>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant={isHighlighted ? "default" : "outline"}
                onClick={handleHighlightToggle}
                disabled={disabled || !hasValue}
                className={cn(
                  "shrink-0",
                  isHighlighted && "bg-yellow-500 hover:bg-yellow-600 dark:bg-yellow-600 dark:hover:bg-yellow-700"
                )}
              >
                <Highlighter className="h-4 w-4" />
                {isHighlighted && <Check className="h-3 w-3 ml-1" />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              {!hasValue ? (
                <p className="text-sm">Select a value first before highlighting</p>
              ) : isHighlighted ? (
                <p className="text-sm">Click to remove highlight from this field</p>
              ) : (
                <p className="text-sm">Click to highlight this field in the PDF report</p>
              )}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      
      {isHighlighted && (
        <p className="text-xs text-yellow-700 dark:text-yellow-400 ml-1">
          This field will be highlighted in the PDF report
        </p>
      )}
    </div>
  );
}